<script setup lang="ts">
const { closeModal } = useLayoutStore();
const authStore = useAuthStore();
const { setLoggedOutByInactivity } = useUserActivityStore();
const { updateWallet } = useWallet();
const { isCheckInactivityEnabled } = useFeatureFlagsJson();

const SECONDS_TO_COUNTDOWN = 120;

const onClickButton = () => {
  closeModal();
  updateWallet();
};

const timerFinished = async () => {
  if (isCheckInactivityEnabled.value) {
    await authStore.logout({
      displayMessage: true,
      redirectToHome: true,
    });

    setLoggedOutByInactivity(true);
  }

  closeModal();
};
</script>

<template>
  <div :class="$style.checkActivity">
    <div :class="$style.title">
      {{ $t('account.check_activity.title') }}
    </div>

    <div :class="$style.subtitle">
      {{ $t('account.check_activity.subtitle') }}
    </div>

    <div :class="$style.label">
      {{ $t('account.check_activity.label') }}
    </div>

    <WidgetsCountdown
      ref="countdownDiv"
      :class="$style.countdownContainer"
      :restart="false"
      :total-time="SECONDS_TO_COUNTDOWN"
      @timer-finished="timerFinished"
    >
      <template #process="{ time }">
        <div :class="$style.timerWrapper">
          <span :class="$style.timer">{{ time.hours }}h</span>
          <span :class="$style.colon">:</span>
          <span :class="$style.timer">{{ time.minutes }}m</span>
          <span :class="$style.colon">:</span>
          <span :class="$style.timer">{{ time.seconds }}s</span>
        </div>
      </template>
      <template #finish>
        <div :class="$style.finishMessage">
          <NuxtIcon
            class="rotate-animation"
            name="loading"
          />
          {{ $t('account.check_activity.disconnected') }}
        </div>
      </template>
    </WidgetsCountdown>

    <div :class="$style.button">
      <button @click="onClickButton">
        <NuxtIcon name="check" />
        {{ $t('account.check_activity.button') }}
      </button>
    </div>

    <div
      :class="$style.explanation"
      v-html="$t('account.check_activity.explanation')"
    />
  </div>
</template>

<style module lang="scss">
.checkActivity {
  @apply p-10 w-full h-full flex flex-col justify-center items-center gap-4 text-auth-texts text-center;

  .title {
    @apply text-2xl font-bold bg-error text-white py-1 px-2;
  }

  .subtitle {
    @apply text-sm opacity-80;
  }

  .label {
    @apply text-sm font-semibold;
  }

  .countdownContainer {
    @apply flex items-center justify-between;

    .timerWrapper {
      @apply flex items-center gap-1;
      .timer {
        @apply p-1 text-base font-bold grid place-items-center rounded bg-white text-black w-[60px] h-[40px];
      }
      .colon {
        @apply text-2xl font-semibold text-auth-texts;
      }
    }

    .finishMessage {
      @apply flex items-center justify-center gap-2 bg-error text-white py-2 px-3 rounded-md h-[40px] text-sm font-medium;

      svg {
        @apply w-4 h-4;
      }
    }
  }

  .button {
    @apply w-full flex items-center justify-center mt-2;
    button {
      @apply bg-auth-button-bg text-auth-button-text py-2 px-3 rounded-md font-bold flex items-center justify-center gap-2;
    }
  }

  .explanation {
    @apply text-2xs opacity-60 px-5;
  }
}
</style>
